import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    output,
    signal,
    untracked,
    ViewEncapsulation,
} from "@angular/core";
import { ReportBuilder, ReportBuilderGroup } from "./s25.document.report.builder.table.component";
import { Doc } from "./s25.document.const";
import { DocumentService } from "../../../services/document.service";
import { ReportBuilderUtil } from "./s25.report.builder.util";
import { s25Resource } from "../../../util/s25.resource";
import { Bind } from "../../../decorators/bind.decorator";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25Util } from "../../../util/s25-util";
import { ReportService } from "../../../services/report.service";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { InstanceCount } from "../../../util/s25.instance.count";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder",
    template: `
        @if (!!document.error()) {
            Something went wrong while loading the document. Please try again later.
        } @else if (document.isLoading() || !reportBuilder() || !type()) {
            <s25-ng-loading-inline-static />
        } @else {
            @if (scope() === "subreport") {
                <div class="section">
                    <label class="reportBuilderLabel ngBold">Header</label>
                    @if (!editable() && !reportBuilder()?.header()?.length) {
                        <p class="addHeaders">None</p>
                    } @else {
                        @if (!!reportBuilder()?.header()?.length) {
                            <s25-ng-document-report-builder-table
                                class="header"
                                [type]="type()!"
                                [scope]="scope()"
                                [(rows)]="reportBuilder()!.header"
                                [section]="'header'"
                                [breakpoints]="breakpoints().header"
                                [allowEmpty]="true"
                                [editable]="editable()"
                            />
                        } @else if (editable()) {
                            <s25-ng-button class="addHeaders" [type]="'primary'" (clicked)="addHeaders()">
                                Add Headers
                            </s25-ng-button>
                        }
                    }
                </div>
            } @else {
                @let groups = reportBuilder()!.groups();
                <div class="groups">
                    @for (group of groups; track $index) {
                        @if (editable() || !!group.groupBy()) {
                            @let groupByLabelId = uniqueId + "-group-" + $index;
                            <div class="section group">
                                <div class="group-by-options">
                                    <div>
                                        <label class="reportBuilderLabel" [class.ngBold]="$first" [id]="groupByLabelId">
                                            @if ($first) {
                                                Group By
                                            } @else {
                                                Then Group By
                                            }
                                        </label>
                                        @if (!editable()) {
                                            <p>{{ groupByNames[group.groupBy()] ?? "None" }}</p>
                                        } @else {
                                            <s25-ng-bpe-variable
                                                [state]="group.state()"
                                                [type]="'occurrencesGroupBy'"
                                                [documentType]="type()!"
                                                [hasPost]="false"
                                                [hasFormatExample]="false"
                                                [prefix]="false"
                                                [wrap]="false"
                                                [hasNoneItem]="true"
                                                [itemFilter]="groupByFilters()[$index]"
                                                [placeholder]="'None'"
                                                [ariaLabel]="{ by: groupByLabelId }"
                                                (variableChange)="setGroupBy(group, $event)"
                                            />
                                        }
                                    </div>
                                    @if (
                                        !!group.state().variable &&
                                        group.state().variable.val !== "monthlyCalendarContinuous"
                                    ) {
                                        <div>
                                            <label>Page Break</label>
                                            <s25-toggle-button [(value)]="group.pageBreak" [disabled]="!editable()" />
                                        </div>
                                    }
                                </div>
                                @if (group.state().variable) {
                                    @if (group.rows().length) {
                                        <div class="headers">
                                            <label class="reportBuilderLabel">Group Headers</label>
                                            <s25-ng-document-report-builder-table
                                                class="header"
                                                [type]="type()!"
                                                [scope]="scope()"
                                                [(rows)]="group.rows"
                                                [section]="'GH_' + ($index + 1)"
                                                [breakpoints]="breakpoints().groups[$index]"
                                                [allowEmpty]="true"
                                                [editable]="editable()"
                                                [groupBy]="group.state().variable.val"
                                            />
                                        </div>
                                    } @else if (editable()) {
                                        <div class="headers">
                                            <s25-ng-button
                                                class="addHeaders"
                                                [type]="'primary'"
                                                (clicked)="addGroupHeaders(group)"
                                            >
                                                Add Group Headers
                                            </s25-ng-button>
                                        </div>
                                    }
                                }
                            </div>
                        }
                    }
                </div>
            }

            <div class="section body">
                <label class="reportBuilderLabel ngBold">Body</label>
                <s25-ng-document-report-builder-table
                    class="body"
                    [type]="type()!"
                    [scope]="scope()"
                    [(rows)]="reportBuilder()!.body"
                    [section]="'body'"
                    [breakpoints]="breakpoints().body"
                    [editable]="editable()"
                />
            </div>
        }
    `,
    styles: `
        :host {
            --border-width: 1px;
            --border-color: #0002;
        }

        .addHeaders {
            display: block;
        }

        :host.nested label,
        :host.nested s25-ng-button {
            display: none;
        }

        :host-context(s25-ng-document-report-builder-table) {
            .reportBuilderLabel,
            .addHeaders {
                display: none;
            }

            s25-ng-document-report-builder-table.header {
                border-bottom: var(--border-width) solid var(--border-color);
                display: block;
            }

            .section {
                padding-block: 0;
            }
        }

        .groups {
            padding-bottom: 1rem;
            margin-bottom: 1rem;
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: 1rem;
        }

        .group {
            grid-column: 1/-1;
            grid-template-columns: subgrid;
            border-bottom: var(--border-width) solid var(--border-color);
        }

        .section {
            display: grid;
            padding-block: 0.5rem;
        }

        .group-by-options {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1/-1;
        }

        .group-by-options s25-ng-bpe-variable {
            min-width: 28ch;
            display: block;
        }

        .group .headers {
            margin-top: 1rem;
            grid-column: 1/-1;
            grid-template-columns: subgrid;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderComponent {
    public static readonly MAX_GROUPS = 3;
    private static nextId = InstanceCount.idGenerator({ prefix: "DocumentReportBuilder" });

    readonly documentId = input.required<number>();
    readonly typeInput = input<Doc.ReportBuilderTypeId | undefined>(undefined, { alias: "type" });
    readonly scopeInput = input<Doc.Scope | undefined>(undefined, { alias: "scope" });
    readonly editable = input<boolean>(true);

    readonly tableStrChange = output<string>();
    readonly reportBuilderChange = output<ReportBuilder>();

    readonly document = s25Resource({
        params: () => this.documentId(),
        loader: ({ params: documentId, abortSignal }) => DocumentService.getDocument(documentId),
    });
    readonly report = s25Resource({
        params: () => this.document.value()?.report_id,
        loader: ({ params: reportId, abortSignal }) => ReportService.getReport(reportId),
    });

    readonly type = computed(() => {
        return this.typeInput() ?? <Doc.ReportBuilderTypeId | undefined>this.document.value()?.document_type;
    });
    readonly scope = computed(() => {
        return this.scopeInput() ?? this.document.value()?.document_scope;
    });

    readonly context = s25Resource(ReportBuilderUtil.getParserData);
    readonly reportBuilder = linkedSignal({
        source: () => ({
            documentId: this.documentId(),
            document: this.document.value(),
            report: this.report.value(),
            type: this.type(),
            context: this.context.value(),
        }),
        computation: ({ documentId, document, report, type, context }, previous): ReportBuilder | undefined => {
            if (!context || !type || (!!documentId && !document) || (!!document?.report_id && !report)) return;

            let builder: ReportBuilder;
            if (!!document && (!previous?.value || previous?.source.document !== document)) {
                untracked(() => {
                    builder = ReportBuilderUtil.parseWithData(
                        {
                            tableString: document.body_text,
                            type,
                            groupBy: document.group_by,
                            pageBreak: document.page_break,
                        },
                        context,
                    );
                });
            } else if (!!previous?.value) {
                builder = { ...previous.value };
            } else {
                builder = ReportBuilderUtil.getDefaultReportBuilder();
            }
            builder!.variable = signal(`ws.flexTable.${type ?? "reservations"}`);

            if (!!previous?.source.type && previous.source.type !== type) {
                // Clear variables on type changes
                untracked(() => {
                    const rows = ReportBuilderUtil.getAllRows(builder);
                    for (const row of rows) {
                        for (const cell of row.cells()) ReportBuilderUtil.clearVariable(cell);
                    }
                });
            }

            return builder!;
        },
    });

    readonly breakpoints = computed(() => {
        const builder = this.reportBuilder();
        if (!builder) return { groups: [], header: [], body: [] };

        const breakpoints = {
            groups: builder.groups().map((group) => ReportBuilderUtil.getBreakpoints(group.rows())),
            header: ReportBuilderUtil.getBreakpoints(builder.header()),
            body: ReportBuilderUtil.getBreakpoints(builder.body()),
        };

        return {
            groups: builder.groups().map((_, i) => {
                return S25Util.array
                    .unique(
                        breakpoints.groups
                            .filter((_, j) => i !== j)
                            .flat()
                            .concat(breakpoints.body),
                    )
                    .sort((a, b) => a - b);
            }),
            header: S25Util.array.unique(breakpoints.body).sort((a, b) => a - b),
            body: S25Util.array.unique(breakpoints.groups.flat()).sort((a, b) => a - b),
        };
    });

    protected readonly groupByFilters = computed(() => {
        const builder = this.reportBuilder();
        if (!builder) return [];

        const restrictions = builder.groups().map((group, i) => {
            const restrictions = {
                blacklist: new Set<string>(),
                whitelist: <Set<string> | undefined>undefined,
            };
            for (const prev of builder.groups().slice(0, i)) {
                const groupBy = prev.groupBy();
                if (!groupBy) continue;
                restrictions.blacklist.add(groupBy);
                let restrictTo: string[] | undefined = undefined;
                if (["weeklyCalendar", "monthlyCalendar", "monthlyCalendarContinuous"].includes(groupBy)) {
                    restrictTo = ["reservation"];
                } else if (groupBy === "reservation") {
                    restrictTo = [];
                }
                if (restrictTo) {
                    restrictions.whitelist = new Set(
                        S25Util.array.intersection(Array.from(restrictions.whitelist ?? restrictTo), restrictTo),
                    );
                }
            }
            return restrictions;
        });

        return (
            this.reportBuilder()
                ?.groups()
                .map((_, i) => {
                    return (items: DropDownItem<string>[]) => {
                        const { blacklist, whitelist } = restrictions[i] ?? {};
                        return items.filter((item) => {
                            if (blacklist && blacklist.has(item.val!)) return false;
                            if (whitelist && !whitelist.has(item.val!)) return false;
                            return true;
                        });
                    };
                }) ?? []
        );
    });

    public readonly valid = computed(() => {
        const builder = this.reportBuilder();
        if (!builder) return false;
        const rows = ReportBuilderUtil.getAllRows(builder);
        return rows.every((row) => {
            return row.cells().every((cell) => cell.valid());
        });
    });

    protected readonly uniqueId = DocumentReportBuilderComponent.nextId();

    constructor() {
        effect(this.outputOnChange);
    }

    @Bind
    outputOnChange() {
        const builder = this.reportBuilder();
        const type = this.type();
        if (!builder || !type) return;
        this.tableStrChange.emit(ReportBuilderUtil.stringify(builder, type));
        this.reportBuilderChange.emit(builder);
    }

    addHeaders() {
        const builder = this.reportBuilder();
        if (!builder) return;
        builder.header.set([
            {
                cells: signal(
                    builder
                        .body()[0]
                        .cells()
                        .map((cell) =>
                            ReportBuilderUtil.newCell({
                                section: "header",
                                width: cell.width(),
                            }),
                        ),
                ),
            },
        ]);
    }

    addGroupHeaders(group: ReportBuilderGroup) {
        const builder = this.reportBuilder();
        if (!builder) return;
        group.rows.set([
            {
                cells: signal(
                    builder
                        .body()[0]
                        .cells()
                        .map((cell) =>
                            ReportBuilderUtil.newCell({
                                section: group.section(),
                                width: cell.width(),
                            }),
                        ),
                ),
            },
        ]);
    }

    protected setGroupBy(group: ReportBuilderGroup, variable: string) {
        group.groupBy.set(variable);

        const builder = this.reportBuilder();
        if (!builder) return;
        builder.groups.update((groups) => {
            const newGroups = groups.filter((group) => !!group.state().variable);
            for (const [i, group] of S25Util.array.enumerate(newGroups)) {
                group.section.set("GH_" + (i + 1));
                group.rows().forEach((row) => row.cells().forEach((cell) => cell.section.set("GH_" + (i + 1))));
            }
            if (newGroups.length < DocumentReportBuilderComponent.MAX_GROUPS) {
                newGroups.push({
                    section: signal("GH_" + (newGroups.length + 1)),
                    groupBy: signal(undefined),
                    pageBreak: signal(false),
                    rows: signal([]),
                    state: signal({ options: {} }),
                });
            }
            return newGroups;
        });

        // Validate group cells after groupBy choice changes
        for (const cell of group.rows().flatMap((row) => row.cells())) {
            ReportBuilderUtil.validateCell(cell, group.groupBy(), { validateCss: false });
        }
    }

    protected groupByNames = S25Util.fromEntries(
        BpeVarsConst.Items.occurrencesGroupBy.map((item) => [item.val, item.txt]),
    );
}
